<template>
  <div class="onb-nav">

    <aside class="sidebar" :class="{ 'is-disabled': isLoading }">
      <AsideMenuLogo route="prime" />

      <div class="xp">
        <span class="xp-icon">🏆</span>
        <div>
          <div class="xp-level">Рівень {{ userLevel }}</div>
          <div class="xp-points">{{ points }} XP</div>
        </div>
      </div>

      <div class="progress-head">
        <span>Прогрес онбордингу</span>
        <strong>{{ progressPct }}%</strong>
      </div>
      <div class="progress-track">
        <div class="progress-fill" :style="{ width: progressPct + '%' }"></div>
      </div>

      <nav class="steps">
        <template v-for="(s, idx) in steps">
          <button
            v-if="!s.optional"
            :key="s.id || idx"
            type="button"
            class="step"
            :class="{
              'is-active': currentStep === idx,
              'is-done': checkStepDone(idx),
              'is-blocked': isStepDisabled(idx)
            }"
            :disabled="isLoading || isStepDisabled(idx)"
            @click="selectStep(idx)"
          >
            <span class="step-num">{{ checkStepDone(idx) ? '✓' : idx }}</span>
            <span class="step-label">{{ s.label }}</span>
            <span
              v-if="isStepDisabled(idx)"
              class="step-lock"
              :title="idx === 9 && !docsGenerated ? 'Спочатку сформуйте документи на кроці 8' : 'Крок недоступний'"
            >🔒</span>
          </button>
        </template>
      </nav>
    </aside>

    <!-- Мобільна шапка (≤768px) -->
    <header class="m-nav" :class="{ 'is-disabled': isLoading }">
      <div class="m-nav-top">
        <AsideMenuLogo route="prime" />
        <div class="m-xp">
          <span>🏆</span>
          <span>Рівень {{ userLevel }}</span>
          <span class="m-xp-points">· {{ points }} XP</span>
        </div>
      </div>

      <button type="button" class="m-step" :disabled="isLoading" @click="sheetOpen = true">
        <span class="m-step-num">{{ currentStep }}</span>
        <span class="m-step-text">
          <span class="m-step-kicker">{{ currentStep === 0 ? 'Старт' : `Крок ${currentStep} з ${lastStepIdx}` }}</span>
          <span class="m-step-label">{{ currentStepLabel }}</span>
        </span>
        <span class="m-step-more">Усі кроки ▾</span>
      </button>

      <div class="progress-track">
        <div class="progress-fill" :style="{ width: progressPct + '%' }"></div>
      </div>
    </header>

    <!-- Мобільна нижня панель з кроками -->
    <transition name="m-sheet">
      <div v-if="sheetOpen" class="m-sheet">
        <div class="m-sheet-backdrop" @click="sheetOpen = false"></div>
        <div class="m-sheet-panel" role="dialog" aria-modal="true">
          <div class="m-sheet-handle"></div>
          <div class="m-sheet-head">
            <span>Прогрес онбордингу</span>
            <button type="button" class="m-sheet-close" aria-label="Закрити" @click="sheetOpen = false">✕</button>
          </div>

          <div class="progress-head">
            <span>Виконано</span>
            <strong>{{ progressPct }}%</strong>
          </div>
          <div class="progress-track">
            <div class="progress-fill" :style="{ width: progressPct + '%' }"></div>
          </div>

          <nav class="steps">
            <template v-for="(s, idx) in steps">
              <button
                v-if="!s.optional"
                :key="'m-' + (s.id || idx)"
                type="button"
                class="step"
                :class="{
                  'is-active': currentStep === idx,
                  'is-done': checkStepDone(idx),
                  'is-blocked': isStepDisabled(idx)
                }"
                :disabled="isLoading || isStepDisabled(idx)"
                @click="selectStep(idx)"
              >
                <span class="step-num">{{ checkStepDone(idx) ? '✓' : idx }}</span>
                <span class="step-label">{{ s.label }}</span>
                <span v-if="isStepDisabled(idx)" class="step-lock">🔒</span>
              </button>
            </template>
          </nav>

          <div class="m-sheet-note">Прогрес збережено автоматично</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import AsideMenuLogo from '@/widgets/app-main-menu/ui/main-menu/aside-menu-desktop/aside-menu-logo';
import profileForm from "@/components/app/profile/profileInfoForms/mixin/profileForm";

export default {
  name: "OnboardingSidebar",
  components: { AsideMenuLogo },
  props: {
    steps: { type: Array, required: true },
    currentStep: { type: Number, required: true },
    points: { type: Number, default: 0 },
    visited: { type: Object, default: () => ({}) },
    userLevel: { type: Number, default: 1 },
    progressPct: { type: Number, default: 0 },
    nextStepLabel: { type: String, default: null },
    checkStepDone: { type: Function, required: true },
    isLoading: { type: Boolean, default: false },
    docsGenerated: { type: Boolean, default: false }
  },
  data() {
    return {
      sheetOpen: false,
    };
  },
  computed: {
    lastStepIdx() {
      return this.steps.filter(s => !s.optional).length - 1;
    },
    currentStepLabel() {
      const s = this.steps[this.currentStep];
      return s ? s.label : '';
    },
  },
  watch: {
    sheetOpen(val) {
      // block scroll on open menu
      document.body.style.overflow = val ? 'hidden' : '';
    },
    currentStep() {
      this.sheetOpen = false;
    },
  },
  mounted() {
    this.onKeydown = (e) => {
      if (e.key === 'Escape') this.sheetOpen = false;
    };
    window.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown);
    document.body.style.overflow = '';
  },
  methods: {
    selectStep(idx) {
      if (this.isLoading) return;

      if (this.isStepDisabled(idx)) {
        if (idx === 9 && !this.docsGenerated) {
          if (this.$message) {
            this.$message.warning('Спочатку необхідно сформувати пакет документів на кроці «Перевірка даних»!');
          }
        }
        return;
      }

      this.sheetOpen = false;
      this.$emit('select-step', idx);
    },
    isStepDisabled(idx) {
      if (idx === 9 && !this.docsGenerated) {
        return true;
      }

      if (idx === this.currentStep) {
        return false;
      }

      return !this.visited || !this.visited[idx];
    },
  }
}
</script>

<style scoped>
.step.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
